import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SlackHomePanels } from "@/components/SlackHomePanels";
import { api, type MailAccount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { matchesHubSearch, useHubSearchQuery } from "@/lib/hubSearch";
import { peekModuleCache, writeModuleCache } from "@/lib/moduleCache";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { displayMailFrom } from "@/mail/formatContact";

const HOME_CACHE_KEY = "home";
const HOME_REFRESH_MS = 60_000;

type HomeMeeting = {
  id?: string;
  title?: string;
  start?: string;
  end?: string;
};

type HomeEvent = {
  id: string;
  title?: string;
  start?: string;
  end?: string;
  location?: string;
  isMeeting?: boolean;
};

type HomeMail = {
  id: string;
  subject?: string;
  unread?: boolean;
  providerName?: string;
  updatedAt?: string;
  date?: string;
  participants?: { name?: string; email?: string }[];
  fromName?: string;
  fromEmail?: string;
};

type HomePayload = {
  unreadMail?: number;
  unreadChat?: number;
  nextMeeting?: HomeMeeting | null;
  mailAccounts?: MailAccount[];
  todayEvents?: HomeEvent[];
  recentMail?: HomeMail[];
};

function greetingName(user: { name?: string; displayName?: string; email?: string } | null) {
  const raw = (user?.name || user?.displayName || user?.email || "there").trim();
  const first = raw.split(/[\s@._-]+/).filter(Boolean)[0] || "there";
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function greetingLine(name: string) {
  const hour = new Date().getHours();
  const part = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return `${part}, ${name}`;
}

function formatLongDate(date = new Date()) {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatRange(start?: string, end?: string) {
  if (!start) return "";
  const from = new Date(start);
  if (Number.isNaN(from.getTime())) return start;
  const sameDayEnd =
    end &&
    !Number.isNaN(new Date(end).getTime()) &&
    new Date(end).toDateString() === from.toDateString();
  const day = from.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const allDay =
    from.getHours() === 0 &&
    from.getMinutes() === 0 &&
    (!end || new Date(end).getHours() === 0);
  if (allDay || !end) return day;
  if (!sameDayEnd) {
    return `${day} · ${from.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })}`;
  }
  const startLabel = from.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  const endLabel = new Date(end).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${day}, ${startLabel} to ${endLabel}`;
}

function formatMailWhen(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function participantLabel(item: HomeMail) {
  const label = displayMailFrom(item);
  return label === "Unknown" ? "" : label;
}

export function HubPage() {
  const { user } = useAuth();
  const [home, setHome] = useState<HomePayload | null>(() => peekModuleCache<HomePayload>(HOME_CACHE_KEY));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(() => !peekModuleCache(HOME_CACHE_KEY));
  const homeRef = useRef(home);
  homeRef.current = home;
  const mountedRef = useRef(true);

  const loadHome = useCallback(async () => {
    try {
      const payload = await api<HomePayload>("/api/home");
      if (!mountedRef.current) return;
      setHome(payload);
      setError("");
      void writeModuleCache(HOME_CACHE_KEY, payload, { mirror: true });
    } catch (err) {
      if (!mountedRef.current) return;
      // Keep the saved snapshot on screen when a refresh fails.
      if (!homeRef.current) setError(err instanceof Error ? err.message : "Could not load Hub.");
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void loadHome();
    return () => {
      mountedRef.current = false;
    };
  }, [loadHome]);

  useLiveRefresh(() => void loadHome(), HOME_REFRESH_MS);

  const name = useMemo(() => greetingName(user), [user]);
  const accounts = home?.mailAccounts || [];
  const accountCount = accounts.length;
  const noMail = accountCount === 0;
  const unreadMail = home?.unreadMail ?? 0;
  const unreadChat = home?.unreadChat ?? 0;
  const nextMeeting = home?.nextMeeting;
  const searchQuery = useHubSearchQuery("home").trim();
  const todayEvents = (home?.todayEvents || []).filter((event) =>
    matchesHubSearch(searchQuery, event.title, event.location),
  );
  const recentMail = (home?.recentMail || []).filter((item) =>
    matchesHubSearch(searchQuery, item.subject, item.fromName, item.fromEmail, item.participants, item.providerName),
  );

  return (
    <div className="page-pad hub-home">
      <header className="hub-home-head">
        <h1>{greetingLine(name)}</h1>
        <p className="muted">
          {formatLongDate()}.{" "}
          {noMail
            ? "No mailboxes connected yet."
            : `${accountCount} mailbox${accountCount === 1 ? "" : "es"} connected on this Mac.`}
        </p>
      </header>

      {error ? (
        <p className="banner-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading && !home ? <p className="muted">Loading Hub…</p> : null}
      {searchQuery ? (
        <p className="banner-info" role="status">
          Showing Hub items that match “{searchQuery}”. Press Enter to search all of Mail.
        </p>
      ) : null}

      <section className="hub-stats" aria-label="Hub summary">
        <Link className="hub-stat" to={noMail ? "/settings/connections" : "/mail"}>
          <span>Unread Mail</span>
          <strong>{unreadMail}</strong>
          {noMail ? <small>Connect an account to see mail</small> : null}
        </Link>
        <Link className="hub-stat" to="/chat">
          <span>Unread Chat</span>
          <strong>{unreadChat}</strong>
        </Link>
        <Link
          className="hub-stat"
          to={nextMeeting?.id ? `/meetings` : "/meetings"}
        >
          <span>Next Meeting</span>
          <strong>{nextMeeting?.title || "None"}</strong>
          {nextMeeting?.start ? (
            <small>{formatRange(nextMeeting.start, nextMeeting.end)}</small>
          ) : null}
        </Link>
        <Link className="hub-stat" to="/settings/connections">
          <span>Accounts</span>
          <strong>{accountCount}</strong>
          <small>{noMail ? "Connect in Settings" : "Manage in Settings"}</small>
        </Link>
      </section>

      <div className="hub-home-split">
        <section className="settings-card hub-panel" id="hub-today">
          <div className="settings-card-head">
            <h2>Today</h2>
            <Link className="hub-panel-link" to="/calendar">
              Open Calendar
            </Link>
          </div>
          {todayEvents.length === 0 ? (
            <p className="muted">
              {searchQuery ? "Nothing on today's calendar matches." : "Nothing else on today's calendar."}
            </p>
          ) : (
            <ul className="hub-plain-list">
              {todayEvents.map((event) => (
                <li key={event.id}>
                  <Link to={event.isMeeting ? "/meetings" : "/calendar"}>
                    <strong>{event.title || "Event"}</strong>
                    <span>
                      {formatRange(event.start, event.end)}
                      {event.location ? ` · ${event.location}` : ""}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="settings-card hub-panel" id="hub-inbox">
          <div className="settings-card-head">
            <h2>Inbox</h2>
            <Link
              className="hub-panel-link"
              to={noMail ? "/settings/connections" : "/mail"}
            >
              {noMail ? "Connect Account" : "Open Mail"}
            </Link>
          </div>
          {noMail ? (
            <p className="muted">
              No mail yet. Open Settings to add a mailbox, then come back to Hub.
            </p>
          ) : recentMail.length === 0 ? (
            <p className="muted">
              {searchQuery ? "No recent mail matches." : "No recent mail in this Hub view."}
            </p>
          ) : (
            <ul className="hub-plain-list">
              {recentMail.map((item) => {
                const who = participantLabel(item);
                const when = formatMailWhen(item.updatedAt || item.date);
                return (
                  <li key={item.id}>
                    <Link to={`/mail?thread=${encodeURIComponent(item.id)}`}>
                      <strong>
                        {item.unread ? (
                          <i className="hub-unread-dot" aria-label="Unread" />
                        ) : null}
                        {item.subject || "(no subject)"}
                      </strong>
                      <span>
                        {[item.providerName, who, when].filter(Boolean).join(" · ")}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <SlackHomePanels />
    </div>
  );
}
